import { Store } from '@ngrx/store';
import {
  Client as ConversationsClient,
  Conversation,
  Message
} from '@twilio/conversations';
import * as ChatActions from './chat.actions';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface NamedRoom {
  id: string;
  name: string;
  maxParticipants?: number;
  participantCount: number;
}

export type Rooms = NamedRoom[];
interface Device {
  deviceId: string;
  // track: LocalVideoTrack | LocalAudioTrack;
  isStreaming: boolean;
}

@Injectable()
export class ChatService {
  private _conversationsClient: ConversationsClient;
  private chatProxies = {}; // saving this in the ngrc state store throws error; so it's gonna live here
  private isMobile: boolean = false;
  private tokenRefreshTimer;

  BASE_URL = environment.api.baseUrl;
  // get room() {
  //   return this._videoRoom;
  // }
  // selectedDevices: { audio: Device; video: Device } = {
  //   audio: { deviceId: null, isStreaming: false },
  //   video: { deviceId: null, isStreaming: false }
  // };

  // connectOptions: ConnectOptions = {
  //   bandwidthProfile: {
  //     video: {
  //       dominantSpeakerPriority: 'standard',
  //       mode: 'grid'
  //       //     clientTrackSwitchOffControl: 'auto',
  //       //     contentPreferencesMode: 'auto'
  //     }
  //   },
  //   // dominantSpeaker: false,
  //   // preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],

  //   // Comment this line if you are playing music.
  //   maxAudioBitrate: 16000,
  //   audio: true,
  //   // Capture 720p video @ 24 fps.
  //   video: { height: 720, frameRate: 24, width: 1280 },
  //   networkQuality: { local: 1, remote: 1 }
  // };

  constructor(
    private readonly store: Store // private readonly drawerService: NzDrawerService
  ) {
    this.isMobile =
      typeof navigator === 'undefined' ||
      typeof navigator.userAgent !== 'string'
        ? false
        : /Mobile/.test(navigator.userAgent);

    // if (this.isMobile) {
    //   this.connectOptions.bandwidthProfile.video.maxSubscriptionBitrate = 2500000;
    // }
    // set devices object from localstorage if not defined
    // this.setAudioDevice(this.audioDeviceId);
    // this.setVideoDevice(this.videoDeviceId);
  }

  // token: string; // temporary, should use the state token
  initClient(token: string) {
    if (!!this._conversationsClient) {
      console.log('conversationClient already initialized');
      return;
    }

    this._conversationsClient = new ConversationsClient(token, {});
    this._conversationsClient.on('connectionStateChanged', state => {
      this.store.dispatch(
        ChatActions.connectionStateChanged({ connectionState: state as any })
      );
    });

    this._conversationsClient.on(
      'conversationJoined',
      async (chat: Conversation) => {
        const participants = (await chat.getParticipants()).map(
          p => p.identity
        );
        this.store.dispatch(
          ChatActions.participantJoined({ participants: { ...participants } })
        );
      }
    );

    this._conversationsClient.on('conversationLeft', async chat => {
      const participants = (await chat.getParticipants()).map(p => p.identity);
      this.store.dispatch(
        ChatActions.participantLeft({ participants: { ...participants } })
      );
    });

    // refresh token every 5 minutes
    this.tokenRefreshTimer = setInterval(
      () => this.store.dispatch(ChatActions.refreshToken()),
      1800000 // 30 min refresh rate
    );
    return !!this._conversationsClient;
  }

  async closeConnection() {
    clearInterval(this.tokenRefreshTimer);

    // const chatProxy = await this.getChat()
    // chatProxy.removeAllListeners()
    this.chatProxies = {};

    this._conversationsClient.shutdown();
    this._conversationsClient = null;
  }

  // chat and conversation are the same thing
  async getChat(chatId: string): Promise<Conversation> {
    if (!chatId) {
      console.error('chatId is missing');
      return null;
    }
    if (!this._conversationsClient) {
      console.error("Can't get Chat before conversationClient initialization");
      return null;
    }
    let chatProxy = this.chatProxies[chatId];
    if (!chatProxy) {
      chatProxy = await this._conversationsClient.getConversationBySid(chatId);
      this.chatProxies[chatId] = chatProxy;
    }
    return chatProxy;
  }

  async setNewToken(token: string) {
    this._conversationsClient = await this._conversationsClient.updateToken(
      token
    );
  }

  async loadMessages(
    chatId,
    { pageSize = 30, anchor = undefined, direction = undefined } = {}
  ) {
    const chat = await this.getChat(chatId);
    const count = await chat.getMessagesCount();
    this.store.dispatch(ChatActions.updateChatCount({ chatId, count }));
    if (anchor > count - 1) return [];
    const messages = await chat.getMessages(
      pageSize,
      anchor ? count - anchor - 1 : undefined,
      direction
    );
    const mappedMsgs = (
      await Promise.all(messages.items.map(i => this.parseMessage(i)))
    ).filter(m => {
      if (m.type === 'videoInvite') {
        return m.expiry > Date.now();
      } else {
        return true;
      }
    });

    // watch for future msgs
    chat.onWithReplay('messageAdded', async m => {
      const parsedMsg = await this.parseMessage(m);

      this.store.dispatch(
        ChatActions.messageAdded({
          chatId: m.conversation.sid,
          message: parsedMsg
        })
      );
    });
    return mappedMsgs;
  }

  private async parseMessage(msg: Message) {
    const files = [];
    if (msg.attachedMedia?.length) {
      const promises = msg.attachedMedia.map(m => m.getContentTemporaryUrl());
      const urls = await Promise.all(promises);
      urls.forEach((url, i) => {
        files.push({
          icon: 'file-text-outline',
          url,
          type: msg.attachedMedia[i].contentType
        });
      });
    }

    if (msg.body?.indexOf('JSON::') === 0) {
      const jsonMsg = JSON.parse(msg.body.replace('JSON::', ''));
      return {
        ...jsonMsg,
        author: msg.author,
        json: jsonMsg
      };
    } else {
      return {
        body: msg.body,
        author: msg.author,
        dateCreated: msg.dateCreated,
        files,
        type: files.length ? 'file' : 'text'
      };
    }
  }

  async sendMessage(chatId: string, message: string, files: File[]) {
    const chat = await this.getChat(chatId);
    const promises = [];
    if (message) {
      promises.push(chat.sendMessage(message));
    }
    (files || []).forEach(file => {
      promises.push(chat.sendMessage({ contentType: file.type, media: file }));
    });
    return await Promise.all(promises);
  }
}
  // VIDEO

//   /**
//    * Get the list of input devices of a given kind.
//    * @param kind - 'audio' | 'video'
//    * @returns {Promise<MediaDeviceInfo[]>} the list of media devices
//    */
//   async getInputDevices(kind) {
//     const media = await navigator.mediaDevices.getUserMedia({
//       audio: kind === 'audio'? true: undefined,
//       video: kind === 'video'? true: undefined,
//     });
    
//     media.getTracks().forEach(t => t.stop());
//     const devices = await navigator.mediaDevices?.enumerateDevices();
//     return devices?.filter(device => device.kind === `${kind}input`);
//   }

//   async joinVideoRoom(auth, chatId) {
//     console.debug('joinVideoRoom', auth, chatId);
//     this._videoRoom = await connect(auth.token, {
//       name: chatId,
//       ...this.connectOptions
//     } as ConnectOptions);
//     this.stopAudio();
//     this.stopVideo();
//     return this._videoRoom;
//   }

//   async leaveRoom() {
//     this.room?.localParticipant?.videoTracks.forEach(t => {
//       t.track.stop();
//       t.unpublish();
//     });

//     this.room?.localParticipant?.audioTracks.forEach(t => {
//       t.track.stop();
//       t.unpublish();
//     });

//     this.room.disconnect();
//     delete this._videoRoom;
//   }

//   async setVideoDevice(deviceId: string) {
//     // const [track] = await createLocalTracks({ video: { deviceId } });
//     localStorage.setItem('care_portals.videoDeviceId', deviceId);
//     // Stop the previous LocalTrack, if present.
//     if (this.selectedDevices.video.isStreaming) {
//       this.stopVideo();
//     } // this.selectedDevices.video.track = track as LocalVideoTrack;
//     this.selectedDevices.video.deviceId = deviceId;
//     this.selectedDevices.video.isStreaming = false;
//   }

//   get videoDeviceId() {
//     return (
//       this.selectedDevices.video.deviceId ||
//       localStorage.getItem('care_portals.videoDeviceId')
//     );
//   }

//   async stopVideo() {
//     this.selectedDevices.video.isStreaming = false;
//     this.room?.localParticipant?.videoTracks.forEach(t => {
//       t.track.stop();
//       t.unpublish();
//     });
//   }

//   async startVideo() {
//     if(!this.selectedDevices?.video?.deviceId){
//       const devices = await this.getInputDevices('video');
//       this.setVideoDevice(devices[0].deviceId)
//     }
//     const track = await createLocalTracks({
//       video: { deviceId: this.videoDeviceId }
//     });
//     this.room?.localParticipant?.publishTrack(track[0], {
//       priority: 'high'
//     });
//     this.selectedDevices.video.isStreaming = true;
//     return track[0] as LocalVideoTrack; // need to return the track to the component to attach to localVideo
//   }

//   // AUDIO FUNCTIONS

//   async setAudioDevice(deviceId: string) {
//     if (!deviceId) return;

//     if (this.selectedDevices.audio.isStreaming) {
//       this.stopAudio();
//     }
//     localStorage.setItem('care_portals.audioDeviceId', deviceId);
//     this.selectedDevices.audio.deviceId = deviceId;
//   }

//   get audioDeviceId() {
//     return (
//       this.selectedDevices.audio.deviceId ||
//       localStorage.getItem('care_portals.audioDeviceId')
//     );
//   }
//   async stopAudio() {
//     // this.selectedDevices?.audio?.track?.stop();
//     // this.room.localParticipant.unpublishTrack(this.selectedDevices?.audio?.track);
//     this.room?.localParticipant?.audioTracks.forEach(t => {
//       t.track.stop();
//       t.unpublish();
//     });

//     this.selectedDevices.audio.isStreaming = false;
//   }

//   async startAudio() {
//     if(!this.selectedDevices?.audio?.deviceId){
//       const devices = await this.getInputDevices('audio');
//       this.setAudioDevice(devices[0].deviceId)
//     }
//     const publishableTrack = await createLocalTracks({
//       audio: { deviceId: this.selectedDevices.audio.deviceId }
//     });
//     this.room?.localParticipant.publishTrack(publishableTrack[0]);
//     this.selectedDevices.audio.isStreaming = true;
//   }

//   printNetworkQualityStats(networkQualityLevel, networkQualityStats) {
//     // Print in console the networkQualityLevel using bars
//     console.log(
//       {
//         1: '▃',
//         2: '▃▄',
//         3: '▃▄▅',
//         4: '▃▄▅▆',
//         5: '▃▄▅▆▇'
//       }[networkQualityLevel] || ''
//     );

//     if (networkQualityStats) {
//       // Print in console the networkQualityStats, which is non-null only if Network Quality
//       // verbosity is 2 (moderate) or greater
//       console.log('Network Quality statistics:', networkQualityStats);
//     }
//   }
// }
